import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@xxtract/ui';
import { useToast } from '@/components/feedback/Toaster';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage } from '@/components/feedback/FilterRejectedMessage';
import { Filterbalk, useStartadres, useTabelData, useTabelStand } from '@/components/tabel';
import { ADMINISTRATION_EMAIL } from '@/lib/contacts';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { useInvoices } from '../hooks/useInvoices';
import { useDownloadInvoicePdf, useDownloadInvoicesZip, invoiceDownloadErrorKey } from '../hooks/useInvoiceDownloads';
import { InvoiceTable } from '../components/InvoiceTable';
import { InvoiceBulkBar } from '../components/InvoiceBulkBar';
import { FACTUREN_SLEUTEL, FACTUUR_LIJST, JAAR_SLEUTEL, jaarLijst, type FactuurContext } from '../factuurLijst';

const PAGE_SIZE = 20;
/** The whole chosen year in one request (API max 500; largest measured year: 151 invoices). */
const YEAR_PAGE_SIZE = 500;
const FILTER_TEST_ID = 'invoices';

function currentYearOptions(): number[] {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1, currentYear - 2];
}

export function InvoicesPage() {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const kopRef = useRef<HTMLHeadingElement>(null);

  const yearOptions = useMemo(currentYearOptions, []);
  // One reader for the page: `?year=` goes to the year page filter, `?status=` etc. to the list (old links keep working).
  const startadres = useMemo(
    () => [
      { lijst: jaarLijst(yearOptions), standSleutel: JAAR_SLEUTEL },
      { lijst: FACTUUR_LIJST, standSleutel: FACTUREN_SLEUTEL },
    ],
    [yearOptions],
  );
  const { start, geweigerd, wisGeweigerd } = useStartadres(startadres);
  const bewaardJaar = useLijstStandStore((s) => s.standen[JAAR_SLEUTEL]?.filters.jaar);
  const jaarFilter = start[JAAR_SLEUTEL]?.filters.jaar ?? bewaardJaar;
  const gekozenJaar = Array.isArray(jaarFilter) ? Number(jaarFilter[0]) : NaN;
  // An invalid year (link or stale state) falls back to the current year.
  const year = yearOptions.includes(gekozenJaar) ? gekozenJaar : yearOptions[0]!;

  const stand = useTabelStand(FACTUUR_LIJST, FACTUREN_SLEUTEL, { start: start[FACTUREN_SLEUTEL], afhankelijkVan: JAAR_SLEUTEL, paginagrootte: PAGE_SIZE });
  const context = useMemo<FactuurContext>(() => ({ t, locale: i18n.language }), [t, i18n.language]);

  // Tenant identity is the session only: no gln/companyId, and no status or page to the server.
  const { data, isLoading, isError, error, refetch } = useInvoices({ year, pageSize: YEAR_PAGE_SIZE });
  const downloadPdf = useDownloadInvoicePdf();
  const downloadZip = useDownloadInvoicesZip();

  const alleFacturen = data?.data ?? [];
  const meta = data?.meta ?? {};
  const tabel = useTabelData(data?.data ?? undefined, FACTUUR_LIJST, stand, context);
  const invoices = tabel.rijen;
  // A filter change can hide selected invoices; they must not stay counted or go into the ZIP.
  // Keyed on the content: without a saved state `stand.filters` is a fresh object every render.
  const filterSleutel = JSON.stringify(stand.filters);
  useEffect(() => setSelectedIds(new Set()), [filterSleutel]);

  // More invoices in the year than loaded: the choice lists say their counts are incomplete.
  const balkData = { ...tabel, afgekapt: (meta.total ?? 0) > alleFacturen.length };

  function handleYearChange(nextYear: number) {
    useLijstStandStore.getState().zetPaginafilter(JAAR_SLEUTEL, { jaar: [String(nextYear)] });
    setSelectedIds(new Set());
  }

  function toggleOne(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((prev) => {
      if (invoices.length > 0 && invoices.every((invoice) => prev.has(invoice.id))) return new Set();
      return new Set(invoices.map((invoice) => invoice.id));
    });
  }

  function handleDownloadPdf(id: string) {
    downloadPdf.mutate(id, {
      onError: (err) => addToast({ key: invoiceDownloadErrorKey(err) }, 'error'),
    });
  }

  function handleDownloadZip() {
    downloadZip.mutate(Array.from(selectedIds), {
      onError: (err) => addToast({ key: invoiceDownloadErrorKey(err) }, 'error'),
    });
  }

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  return (
    <div className="space-y-5" data-testid="invoices-page">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)] outline-none" ref={kopRef} tabIndex={-1}>{t('invoices.title')}</h1>
        {meta.companyName != null && (
          <p className="mt-1 text-sm text-[var(--foreground-muted)]">
            {meta.debtorNumber != null
              ? t('invoices.subtitleWithDebtor', { company: meta.companyName, debtorNumber: meta.debtorNumber })
              : meta.companyName}
          </p>
        )}
      </header>

      {!meta.noBillingLink && (
        <div className="flex items-start gap-2.5 rounded-[10px] border border-[var(--border)] bg-[var(--info-light,var(--background-muted))] p-3 px-4">
          <span className="material-symbols-outlined shrink-0 text-[16px] text-[var(--secondary)]" aria-hidden="true">info</span>
          <p className="text-xs text-[var(--foreground)]">
            {t('invoices.contactBanner.text')}{' '}
            <a className="font-medium underline" href={`mailto:${ADMINISTRATION_EMAIL}`}>{ADMINISTRATION_EMAIL}</a>
          </p>
        </div>
      )}

      {meta.noBillingLink ? (
        <div data-testid="invoices-no-billing-link" className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-16 text-center">
          <span className="material-symbols-outlined text-[40px] text-[var(--foreground-muted)]" aria-hidden="true">link_off</span>
          <p className="text-sm font-semibold text-[var(--foreground)]">{t('invoices.empty.noBillingLinkTitle')}</p>
          <p className="max-w-md text-xs text-[var(--foreground-muted)]">
            {t('invoices.empty.noBillingLinkDescription', { email: ADMINISTRATION_EMAIL })}
          </p>
        </div>
      ) : meta.degraded ? (
        <div data-testid="invoices-degraded" className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-16 text-center">
          <span className="material-symbols-outlined text-[40px] text-[var(--warning)]" aria-hidden="true">cloud_off</span>
          <p className="text-sm font-semibold text-[var(--foreground)]">{t('invoices.degraded.title')}</p>
          <p className="max-w-md text-xs text-[var(--foreground-muted)]">{t('invoices.degraded.description')}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()}>{t('common.retry')}</Button>
        </div>
      ) : (
        <>
          <FilterRejectedMessage
            baseTestId={FILTER_TEST_ID}
            focusNaWissen={kopRef}
            linkGeweigerd={geweigerd}
            onClear={() => {
              stand.reset();
              wisGeweigerd();
              setSelectedIds(new Set());
            }}
          />
          <Filterbalk
            context={context}
            data={balkData}
            lijst={FACTUUR_LIJST}
            paginafilters={
              <select
                aria-label={t('invoices.filters.yearLabel')}
                value={year}
                onChange={(event) => handleYearChange(Number(event.target.value))}
                className="h-9 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              >
                {yearOptions.map((yearOption) => (
                  <option key={yearOption} value={yearOption}>{yearOption}</option>
                ))}
              </select>
            }
            stand={stand}
          />

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="skeleton h-10 rounded-lg" />
              ))}
            </div>
          ) : alleFacturen.length === 0 ? (
            <div data-testid="invoices-empty-year" className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-16 text-center">
              <span className="material-symbols-outlined text-[40px] text-[var(--foreground-muted)]" aria-hidden="true">receipt_long</span>
              <p className="text-sm text-[var(--foreground-muted)]">{t('invoices.empty.noInvoicesThisYear')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[var(--border)]">
              <InvoiceTable
                sortering={stand.sortering}
                onSorteer={stand.klikOpKop}
                invoices={invoices}
                selectedIds={selectedIds}
                onToggleOne={toggleOne}
                onToggleAll={toggleAll}
                onDownloadPdf={handleDownloadPdf}
                downloadingId={downloadPdf.isPending ? (downloadPdf.variables ?? null) : null}
                locale={i18n.language}
              />
              {invoices.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-[var(--foreground-muted)]" data-testid="invoices-no-results">{t('common.noResults')}</p>
              )}
            </div>
          )}

          {tabel.paginas > 1 && (
            <div className="flex items-center justify-center gap-3 text-sm text-[var(--foreground-muted)]">
              <Button variant="outline" size="sm" disabled={tabel.pagina <= 1} onClick={() => stand.zetPagina(tabel.pagina - 1)}>
                {t('invoices.pagination.previous')}
              </Button>
              <span>{t('invoices.pagination.pageInfo', { page: tabel.pagina, totalPages: tabel.paginas })}</span>
              <Button variant="outline" size="sm" disabled={tabel.pagina >= tabel.paginas} onClick={() => stand.zetPagina(tabel.pagina + 1)}>
                {t('invoices.pagination.next')}
              </Button>
            </div>
          )}
        </>
      )}

      <InvoiceBulkBar
        selectedCount={selectedIds.size}
        onDownloadZip={handleDownloadZip}
        onDeselect={() => setSelectedIds(new Set())}
        isDownloading={downloadZip.isPending}
      />
    </div>
  );
}
